import { CSSProperties, useLayoutEffect, useMemo, useRef, useState } from 'react';

export type MenuAlignType = 'start' | 'end' | 'right' | 'left' | 'center' | 'justify' | 'auto';
export type DropDirectionType = 'up' | 'down' | 'start' | 'end';
type PositionStyles = CSSProperties & {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    transform?: string;
    width?: string;
};

const useDropdownPosition = (drop: DropDirectionType = 'down', menuAlign: MenuAlignType = 'start') => {
    const menuRef = useRef<HTMLUListElement>(null);
    const [isCalculating, setIsCalculating] = useState<boolean>(true);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const initialStyles = useMemo((): PositionStyles => {
        const styles: PositionStyles = {
            top: 'auto',
            bottom: 'auto',
            left: 'auto',
            right: 'auto',
            transform: 'none'
        };

        const dropPos = {
            down: { top: '100%' },
            up: { bottom: '100%' },
            start: { right: '100%', top: '0' },
            end: { left: '100%', top: '0' }
        };

        Object.assign(styles, dropPos[drop]);

        if (drop === 'up' || drop === 'down') {
            if (menuAlign === 'start' || menuAlign === 'left') {
                styles.left = '0';
            } else if (menuAlign === 'end' || menuAlign === 'right') {
                styles.right = '0';
            } else if (menuAlign === 'center') {
                styles.left = '50%';
                styles.transform = 'translateX(-50%)';
            }
        }

        return styles;
    }, [drop, menuAlign]);

    const [styles, setStyles] = useState<PositionStyles>(initialStyles);

    useLayoutEffect(() => {
        if (!isOpen) {
            setIsCalculating(true);
            setStyles(initialStyles);

            return;
        }

        const handleInitialPosition = () => {
            if (!menuRef.current) {
                return;
            }

            const rect = menuRef.current.getBoundingClientRect();
            const vh = window.innerHeight;
            const vw = window.innerWidth;
            const finalStyles = { ...initialStyles };

            if (rect.bottom > vh && drop === 'down') {
                finalStyles.top = 'auto';
                finalStyles.bottom = '100%';
            } else if (rect.top < 0 && drop === 'up') {
                finalStyles.bottom = 'auto';
                finalStyles.top = '100%';
            }

            if (rect.right > vw) {
                finalStyles.left = 'auto';
                finalStyles.right = '0';
            } else if (rect.left < 0) {
                finalStyles.right = 'auto';
                finalStyles.left = '0';
            }

            setStyles(finalStyles);
            setIsCalculating(false);
        };

        handleInitialPosition();

        const onClose = () => {
            setIsOpen(false);
        };

        window.addEventListener('resize', onClose);

        return () => window.removeEventListener('resize', onClose);
    }, [isOpen, drop, menuAlign, initialStyles]);

    return {
        isOpen,
        setIsOpen,
        menuRef,
        styles,
        isCalculating
    };
};

export default useDropdownPosition;
